import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faExclamationTriangle, faUser, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { Col, Row, Card, Button, Table, Spinner, Alert, Modal, Badge, Tooltip, OverlayTrigger } from '@themesberg/react-bootstrap';
import { collection, getDocs, query, orderBy, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Link } from 'react-router-dom';

export default () => {
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedError, setSelectedError] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [usersMap, setUsersMap] = useState({});

    useEffect(() => {
        loadErrors();
    }, []);

    const translateErrorMessage = (message) => {
        if (!message) return "Bilinmeyen hata";
        const msg = message.toLowerCase();

        if (msg.includes("user cancelled")) return "Kullanıcı ödemeyi iptal etti";
        if (msg.includes("item already owned")) return "Ürün zaten satın alınmış";
        if (msg.includes("item unavailable")) return "Ürün mevcut değil";
        if (msg.includes("developer error")) return "Geliştirici hatası (Yapılandırma sorunu)";
        if (msg.includes("billing unavailable")) return "Ödeme hizmeti kullanılamıyor";
        if (msg.includes("network error")) return "Ağ hatası";
        if (msg.includes("service disconnected")) return "Hizmet bağlantısı kesildi";
        if (msg.includes("service unavailable")) return "Hizmet kullanılamıyor";
        if (msg.includes("ensure the app is signed correctly")) return "Uygulama imza hatası veya yapılandırma eksik";

        return message; // Eşleşme yoksa orijinali döndür
    };

    const loadErrors = async () => {
        try {
            setLoading(true);
            setLoadError(null);

            const q = query(collection(db, 'billingErrors'));
            const snapshot = await getDocs(q);
            const errorsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })).sort((a, b) => {
                const dateA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp || 0);
                const dateB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp || 0);
                return dateB.getTime() - dateA.getTime();
            });

            setErrors(errorsList);

            // Kullanıcı bilgilerini çek (Tekilleştirerek)
            const userIds = [...new Set(errorsList.map(e => e.userId).filter(id => id))];
            const userData = {};

            await Promise.all(userIds.map(async (uid) => {
                try {
                    const userDoc = await getDoc(doc(db, 'users', uid));
                    if (userDoc.exists()) {
                        userData[uid] = userDoc.data();
                    }
                } catch (e) {
                    console.error("User fetch error:", uid, e);
                }
            }));

            setUsersMap(userData);

        } catch (err) {
            console.error("Error loading billing errors:", err);
            setLoadError("Hatalar yüklenirken sorun oluştu: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedError) return;
        try {
            setDeleting(true);
            await deleteDoc(doc(db, 'billingErrors', selectedError.id));
            setErrors(errors.filter(e => e.id !== selectedError.id));
            setShowDeleteModal(false);
            setSelectedError(null);
        } catch (err) {
            console.error("Error deleting error log:", err);
            setLoadError("Silme işlemi başarısız: " + err.message);
        } finally {
            setDeleting(false);
        }
    };

    const formatDate = (date) => {
        if (!date) return "N/A";
        const d = date.toDate ? date.toDate() : new Date(date);
        return d.toLocaleString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    return (
        <>
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                <div className="d-block mb-4 mb-md-0">
                    <h4>Ödeme Hataları (Pay Error)</h4>
                    <p className="mb-0">Uygulama içindeki ödeme hatalarının takibini yapın.</p>
                </div>
                <div className="btn-toolbar mb-2 mb-md-0">
                    <Button variant="outline-primary" size="sm" onClick={loadErrors} disabled={loading}>
                        Yenile
                    </Button>
                </div>
            </div>

            {loadError && (
                <Alert variant="danger" dismissible onClose={() => setLoadError(null)}>
                    {loadError}
                </Alert>
            )}

            <Card border="light" className="table-wrapper table-responsive shadow-sm">
                <Card.Body className="pt-0">
                    {loading ? (
                        <div className="d-flex justify-content-center p-5">
                            <Spinner animation="border" />
                        </div>
                    ) : (
                        <Table hover className="user-table align-items-center">
                            <thead>
                                <tr>
                                    <th className="border-bottom">Kullanıcı</th>
                                    <th className="border-bottom">Hata Tipi</th>
                                    <th className="border-bottom">Hata Mesajı (TR)</th>
                                    <th className="border-bottom">Cihaz / Versiyon</th>
                                    <th className="border-bottom">Tarih</th>
                                    <th className="border-bottom">İşlem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {errors.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center p-4">Henüz hata kaydı bulunmuyor.</td>
                                    </tr>
                                ) : (
                                    errors.map(error => {
                                        const user = usersMap[error.userId];
                                        return (
                                            <tr key={error.id}>
                                                <td>
                                                    {user ? (
                                                        <Link to={`/members/edit/${error.userId}`} className="d-flex align-items-center">
                                                            <div className="d-block">
                                                                <span className="fw-bold">{user.name || "İsimsiz"}</span>
                                                                <div className="small text-muted">{user.email || "E-posta yok"}</div>
                                                            </div>
                                                        </Link>
                                                    ) : (
                                                        <span className="text-muted">
                                                            <FontAwesomeIcon icon={faUser} className="me-1" />
                                                            {error.userId || "Bilinmeyen"}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <Badge bg="danger" className="me-1">{error.errorType || "Error"}</Badge>
                                                    {error.responseCode !== undefined && (
                                                        <Badge bg="secondary">Kod: {error.responseCode}</Badge>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <span className="text-wrap" style={{ maxWidth: '250px' }}>
                                                            {translateErrorMessage(error.errorMessage)}
                                                        </span>
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip>Orijinal: {error.errorMessage}</Tooltip>}
                                                        >
                                                            <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-info cursor-pointer" />
                                                        </OverlayTrigger>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="small">
                                                        <div>{error.deviceInfo?.manufacturer} {error.deviceInfo?.model}</div>
                                                        <div className="text-muted">Android: {error.deviceInfo?.androidVersion} | App: {error.deviceInfo?.appVersion}</div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="fw-normal">{formatDate(error.timestamp)}</span>
                                                </td>
                                                <td>
                                                    <Button variant="link" className="text-danger m-0 p-0" onClick={() => {
                                                        setSelectedError(error);
                                                        setShowDeleteModal(true);
                                                    }}>
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            <Modal show={showDeleteModal} onHide={() => !deleting && setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Hata Günlüğünü Sil</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Bu hata kaydını silmek istediğinize emin misiniz?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                        İptal
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                        {deleting ? <Spinner animation="border" size="sm" /> : "Sil"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};
